import { lazy } from 'react';
import { RouteObject } from 'react-router-dom';

import { BaseModule } from 'lib/modules';
import { PROCESSOR_WRITE_ROLES } from 'lib/userHelpers';

const WildGameSettingsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "wild-game-settings" */ 'pages/processor/wild-game'
    ),
);

/**
 * Wild Game module (limited release, PRO-692).
 *
 * Registered so `moduleRegistry.isModuleActive('WILD_GAME')` can answer — which
 * `ModuleProtectedRoute` and the nav's `requiredModule` both depend on, and
 * which silently answers FALSE for any module that was never registered. Phase
 * 2 did not need this: the feature lived entirely inside the jobs surface and
 * gated itself through `useWildGameEnabled`, which reads the user's vendor
 * modules directly. Phase 5 gives wild game a settings page of its own, so the
 * registry now has to know about it too.
 *
 * Note there are two ways to ask "is wild game on?", and they are not
 * interchangeable: `useWildGameEnabled` (user state) for hiding controls inside
 * a page, and the registry for routing and nav. Prefer the hook everywhere
 * except routes.
 */
class WildGameModuleClass extends BaseModule {
  id = 'WILD_GAME';
  name = 'Wild Game';
  label = 'Wild Game Processing';
  description = 'Hunter drop-offs, kiosk access, and game cut sheets';
  version = '1.0.0';
  /**
   * Settings → Wild Game.
   * @param ProtectedRoute - Auth-only guard, used when no module guard is given.
   * @param LazyWrapper - Suspense wrapper supplied by the router.
   * @param settings - Unused here; part of the module route contract.
   * @param ModuleProtectedRoute - Guard that also checks the module is active.
   * @returns The module's routes.
   */
  createRoutes = (
    ProtectedRoute: any,
    LazyWrapper: any,
    settings?: any,
    ModuleProtectedRoute?: any,
  ): RouteObject[] => {
    const RouteProtector = ModuleProtectedRoute || ProtectedRoute;

    return [
      {
        path: '/processor/wild-game',
        element: (
          <RouteProtector
            component={WildGameSettingsPage}
            moduleId="WILD_GAME"
            // WRITE, not view. The page's only content is the kiosk access
            // card, and every control on it writes: minting an address creates
            // a live credential, and "Get a new address" rotates it — which
            // kills every printed sign and every lobby tablet at the plant at
            // once. `assertVendorAccess` checks vendor membership, not role, so
            // this route is what keeps a read-only account away from it.
            allowedUserRoles={PROCESSOR_WRITE_ROLES}
          />
        ),
      },
    ];
  };
}

export const WildGameModule = new WildGameModuleClass();
